import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { OrganizationProvider } from './context/OrganizationContext';

// Eagerly loaded (needed immediately)
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import MainLayout from './components/layouts/MainLayout';
import AuthLayout from './components/layouts/AuthLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { TooltipProvider } from './components/ui/tooltip';
import { ToastProvider, useToast } from './components/ui/toast';
import { setToastHandler } from './services/apiClient';

// Lazy-loaded route components
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Fighters = React.lazy(() => import('./components/dashboard/Fighters'));
const EventDetail = React.lazy(() => import('./pages/EventDetail'));
const VideoAnalyzer = React.lazy(() => import('./pages/AnalyseVideo'));
const Events = React.lazy(() => import('./components/dashboard/Events'));
const HistoryAnalysis = React.lazy(() => import('./components/dashboard/VideoAnalysis'));
const LiveAnalysis = React.lazy(() => import('./pages/LiveAnalysis'));
const VideoAnalytics = React.lazy(() => import('./pages/VideoAnalytics'));
const FighterProfile = React.lazy(() => import('./pages/FighterDetails'));
const PublicEventPage = React.lazy(() => import('./pages/PublicEventPage'));

// Component to connect toast system with API client
const AppWithToast: React.FC = () => {
  const { addToast } = useToast();
  
  // Set up the toast handler for the API client
  React.useEffect(() => {
    setToastHandler(addToast);
  }, [addToast]);

  return (
    <Router>
      <AuthProvider>
        <OrganizationProvider>
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" /></div>}>
            <Routes>
              {/* Auth routes */}
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<Login />} />
                <Route path="/" element={<Login />} />
              </Route>

              {/* Public shareable event page (no login required) */}
              <Route path="/share/events/:token" element={<PublicEventPage />} />

              {/* Main app routes with MainLayout */}
              <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/events" element={<Events />} />
                <Route path="/fighters" element={<Fighters />} />
                <Route path="/fighters/:id" element={<FighterProfile />} />
                <Route path='/video-analyzer' element={<VideoAnalyzer />} />
                <Route path='/history' element={<HistoryAnalysis />} />
                <Route path="/events/:id" element={<EventDetail />} />
                <Route path="/analytics" element={<LiveAnalysis />} />
                <Route path="/video-analytics/:id" element={<VideoAnalytics />} />
              </Route>
              {/* 404 page */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </OrganizationProvider>
      </AuthProvider>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <TooltipProvider>
        <ToastProvider>
          <AppWithToast />
        </ToastProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
};

export default App;